import api from '@common/api';
import auth from '@common/auth';
import engagement from '@common/engagement';
import { PartnersModel } from '@common/models/PartnersModel';
import { default as paramModule, default as params } from '@common/params';
import support from '@common/support';
import tracking from '@common/tracking';
import { isValidEmail, isValidUSPhoneNumber } from '@common/utility/validations';
import { Partner } from '@services/appsync/types';
import { autoSignIn, fetchAuthSession } from 'aws-amplify/auth';
import create from 'zustand';

type EmailValidationState = 'VALID' | 'INVALID_FORMAT' | 'UNAVAILABLE' | null;
interface SignUpModelProps {
  code?: string;
  email?: string;
  emailValidationState: EmailValidationState;
  firstName?: string;
  lastName?: string;
  page: 'EMAIL' | 'NAME' | 'PASSWORD' | 'PARTNER' | 'PHONE' | 'CODE' | 'FINISH' | 'LOADING';
  password?: string;
  phone?: string;
  phoneSubmitted?: boolean;
  totalSteps: 5;
  partner?: Partner;
}

interface SignUpModelActions {
  clear: () => void;
  reset: () => void;
  checkEmail: (email: string) => Promise<void>;
  complete: () => Promise<{ success: boolean; error?: string }>;
  init: (params: URLSearchParams) => Promise<void>;
  nextScreen: (props: {
    email?: string;
    emailValidationState?: EmailValidationState;
    password?: string;
    fname?: string;
    lname?: string;
    phone?: string;
  }) => void;
  resendCode: () => Promise<{ success: boolean; error?: string }>;
  setCode: (code: string) => Promise<{ success: boolean; error?: string }>;
  setEmail: (email: string) => Promise<{ success: boolean; error?: string }>;
  setName: (firstName: string, lastName: string) => void;
  setPassword: (password: string) => void;
  setPhone: (phone: string) => Promise<{ success: boolean; error?: string }>;
  setPhoneSubmitted: (phoneSubmitted: boolean) => void;
}

export const pageNameApp: Record<SignUpModelProps['page'], string> = {
  CODE: 'Code',
  EMAIL: 'Email',
  FINISH: 'Finish',
  LOADING: 'Sign Up',
  NAME: 'Name',
  PARTNER: 'Partner',
  PASSWORD: 'Password',
  PHONE: 'Phone',
};

export const useSignUpModel = create<SignUpModelProps & SignUpModelActions>((set, get) => ({
  checkEmail: async (email) => {
    if (!isValidEmail(email)) {
      set({ emailValidationState: 'INVALID_FORMAT' });
      return;
    }
    const available = await api.isEmailAvailable({ email });
    if (!available) {
      set({ emailValidationState: 'UNAVAILABLE' });
      return;
    } else {
      set({ emailValidationState: 'VALID' });
    }
  },
  clear: () => {
    set({
      email: undefined,
      firstName: undefined,
      lastName: undefined,
      password: undefined,
      phone: undefined,
    });
  },
  code: undefined,
  complete: async () => {
    const { email, password } = get();
    const partner = params.getItem('partner');

    // sign out the user to be safe
    await auth.signOut();

    // sign the user in
    await auth.signIn({ password: password ?? '', username: email ?? '' });
    const user = await fetchAuthSession();

    // instantiate some basic user-services
    tracking.start();
    support.start();

    //identify as appropriate
    if (partner) {
      tracking.identify({ traits: { lead_status: 'pql', partner_id: partner }, userId: user.userSub });
    } else {
      tracking.identify({ userId: user.userSub });
    }
    tracking.track({ action: 'User Creation Completed' });
    set({ page: 'FINISH' });
    return { success: true };
  },
  email: undefined,
  emailValidationState: null,
  firstName: undefined,
  init: async (params) => {
    get().clear(); // start with a clean form values
    await auth.signOut();

    const partnerKey = params.get('partner');

    if (partnerKey !== null) {
      const { data: partners } = await PartnersModel.getState().fetchPartners();
      const partner = partners?.find((p) => p.partner_id.toLowerCase() === partnerKey.toLowerCase());

      if (!partner) {
        set({ page: 'EMAIL' });
        return;
      }

      paramModule.setItem('partner', partner.partner_id);
      const email = params.get('email') ?? undefined;

      let fname = params.get('fname') ?? params.get('first') ?? undefined;
      let lname = params.get('lname') ?? params.get('last') ?? undefined;
      if (fname) fname = fname.charAt(0).toUpperCase() + fname.slice(1);
      if (lname) lname = lname.charAt(0).toUpperCase() + lname.slice(1);

      let phone = params.get('phone') ?? undefined;

      if (email) {
        await get().checkEmail(email);
      }
      if (phone) {
        phone = phone.replace(/-/g, '');
        phone = phone.length < 10 ? undefined : `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}`;

        if (phone && !isValidUSPhoneNumber(phone)) phone = undefined;
      }
      if (!phone && !fname && !lname && !email) {
        set({ page: 'EMAIL', partner });
      } else {
        set({ email, firstName: fname, lastName: lname, page: 'PARTNER', partner, phone: phone });
      }
    } else {
      set({ page: 'EMAIL' });
    }
  },
  lastName: undefined,
  nextScreen: async ({
    email = get().email,
    emailValidationState = get().emailValidationState,
    password = get().password,
    fname = get().firstName,
    lname = get().lastName,
    phone = get().phone,
  }) => {
    if (!email || emailValidationState !== 'VALID') return set({ page: 'EMAIL' });
    if (!password) return set({ page: 'PASSWORD' });
    if (!fname || !lname) return set({ page: 'NAME' });
    if (!phone) return set({ page: 'PHONE' });
    const setPhoneResult = await get().setPhone(phone);
    if (!setPhoneResult.success) {
      set({ page: 'PHONE', phone: undefined, phoneSubmitted: false });
    }
  },
  page: 'LOADING',
  password: undefined,
  phone: undefined,
  phoneSubmitted: false,
  resendCode: async () => {
    const { email } = get();
    const result = await auth.resendCode(email ?? '');
    if (result.success) return { success: true };
    return { error: 'System error', success: false };
  },
  reset: () => {
    set({
      email: undefined,
      firstName: undefined,
      lastName: undefined,
      page: 'EMAIL',
      password: undefined,
      phone: undefined,
    });
  },
  setCode: async (code: string) => {
    const { complete, email } = get();
    const result = await auth.confirmSignUp({ code, email: email ?? '' });

    if (result) {
      tracking.track({ action: 'User Phone Verified' });
      return await complete();
    } else {
      return {
        error: 'The code you entered is invalid. Please make sure you entered it correctly.',
        success: false,
      };
    }
  },
  setEmail: async (email: string) => {
    await auth.signOut();

    const emailAvail = await api.isEmailAvailable({ email: email });
    if (emailAvail) {
      set({ email: email?.toLowerCase(), emailValidationState: 'VALID' });
      tracking.identify({
        traits: {
          email: email,
          lead_status: 'lead',
        },
      });
      get().nextScreen({ email, emailValidationState: 'VALID' });
      tracking.track({ action: 'User Creation Started' });
      engagement.setAnonymousUser(email);
      return { success: true };
    } else {
      return {
        error:
          "Email is already associated with an account. If you have an account with us through our mobile app, you're already signed up. You can use your same credentials to sign in here on our web app.",
        success: false,
      };
    }
  },
  setName: (firstName: string, lastName) => {
    set({ firstName, lastName });
    get().nextScreen({ fname: firstName, lname: lastName });
  },
  setPassword: (password: string) => {
    set({ password });
    get().nextScreen({ password });
  },
  setPhone: async (phone: string) => {
    const { complete, email, firstName, lastName, password, phoneSubmitted, setPhoneSubmitted } = get();

    // if they reached here in error, start them over
    if (!email || !firstName || !lastName || !password) {
      set({ page: 'EMAIL' });
      return { error: 'Missing required fields', success: false };
    }

    // clean the phone number
    const cleanPhone = phone?.replace(/[^\d]/g, '') ?? '';

    // if user has already submitted the phone number we need to rerun this logic
    if (phoneSubmitted) {
      await auth.signOut();
    }

    // sign up the user
    const { signupResult } = await auth.signUp({
      attributes: {
        email: email,
        familyName: lastName,
        givenName: firstName,
        phoneNumber: cleanPhone,
      },
      password: password,
      username: email,
    });

    // since changing the phone number results in an error we need to track it's submission
    setPhoneSubmitted(true);

    // Check the signup result to see if the user needs to confirm via SMS
    if (signupResult?.nextStep?.signUpStep === 'CONFIRM_SIGN_UP') {
      set({ page: 'CODE', phone: phone });
      return { success: true };
    } else if (signupResult?.nextStep?.signUpStep === 'COMPLETE_AUTO_SIGN_IN') {
      const autoSignInResult = await autoSignIn();
      if (autoSignInResult?.nextStep?.signInStep !== 'DONE') {
        throw new Error('Sign in attempt failed unexpectedly.');
      }
    } else {
      throw new Error('Sign up attempt failed unexpectedly.');
    }
    return complete();
  },
  setPhoneSubmitted: (phoneSubmitted: boolean) => {
    set({ phoneSubmitted });
  },
  totalSteps: 5,
}));
